import type { DashboardWidget } from '@sea-snag/shared';
import {
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';

import { useOrganization } from '../../contexts/organization-context';
import { boatsApi } from '../../lib/api-client';
import { queryKeys, STALE_TIMES } from '../../lib/query-keys';
import { updateWidgetConfig } from '../../stores/dashboard-store';
import type { ConfigField, WidgetRegistration } from './widget-registry';

// ── Year options ─────────────────────────────────────────────────────

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => {
  const year = currentYear - i;
  return { label: year.toString(), value: year.toString() };
});

// ── Config Popover ───────────────────────────────────────────────────

interface WidgetConfigPopoverProps {
  widget: DashboardWidget;
  registration: WidgetRegistration;
  children: React.ReactNode;
}

export function WidgetConfigPopover({ widget, registration, children }: WidgetConfigPopoverProps) {
  const { organization } = useOrganization();
  const orgPid = organization?.pid ?? '';

  // Only fetch boats if there's a boat config field
  const hasBoatField = registration.configFields.some((f) => f.type === 'boat');

  const { data: boatsData } = useQuery({
    queryKey: queryKeys.boats.list(orgPid, 'config-popover'),
    queryFn: () => boatsApi.list({ limit: 100 }),
    enabled: !!orgPid && hasBoatField,
    staleTime: STALE_TIMES.LONG,
  });

  const boats = boatsData?.data ?? [];

  function handleChange(field: ConfigField, value: string) {
    updateWidgetConfig(widget.id, { [field.key]: value || undefined });
  }

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-4">
        <p className="text-sm font-medium">Configure Widget</p>
        {registration.configFields.map((field) => (
          <div key={field.key} className="space-y-1.5">
            <Label className="text-xs">{field.label}</Label>
            {field.type === 'year' && (
              <Select
                value={(widget.config[field.key] as string) ?? ''}
                onValueChange={(v) => handleChange(field, v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select year" />
                </SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {field.type === 'boat' && (
              <Select
                value={(widget.config[field.key] as string) ?? ''}
                onValueChange={(v) => handleChange(field, v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All boats" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All boats</SelectItem>
                  {boats.map((boat) => (
                    <SelectItem key={boat.id} value={boat.id}>
                      {boat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
